import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { ResortHero } from "@/components/ResortHero";
import { ResortSearchForm } from "@/components/ResortSearchForm";
import { useState, useMemo, useEffect, useRef, startTransition } from "react";
import ProductCard from "@/components/ProductCard";
import { HotelFiltersSidebar } from "@/components/HotelFiltersSidebar";
import { LazyHotelsMapModal } from "@/components/LazyHotelsMapModal";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { IterpecSearchResponse, IterpecHotel } from "@/types/iterpec";
import { ResortSearchParams, RESORT_DESTINATIONS } from "@/types/resort";
import { useCart } from "@/hooks/useCart";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchParams, useNavigate } from "@/lib/react-router-dom-shim";
import { Button } from "@/components/ui/button";
import { Map, Search, Palmtree, Lightbulb, RefreshCw } from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { translateBoardType } from "@/utils/boardTypeTranslations";
import { SchemaOrg, getBreadcrumbSchema, getOrganizationSchema, getWebSiteSchema, getResortServiceSchema } from "@/components/seo/SchemaOrg";
import { HreflangTags } from "@/components/seo/HreflangTags";
import { useTranslation } from "react-i18next";
import { usePrice } from "@/hooks/usePrice";
import { getMobileOptimizedUrl } from "@/utils/imageOptimization";
import { useLocale } from "@/contexts/LocaleContext";
import { LocaleKey } from "@/i18n";
import { getLocalizedUrl } from "@/hooks/useSeoMeta";
import { useRecommendedHotels } from "@/hooks/useRecommendedHotels";

const Resorts = () => {
  const { t, i18n } = useTranslation();
  const { locale } = useLocale();
  const { format: formatPrice, currency } = usePrice();
  const [searchResults, setSearchResults] = useState<IterpecHotel[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchParams, setSearchParams] = useState<ResortSearchParams | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const { toast } = useToast();
  const [searchTimestamp, setSearchTimestamp] = useState<Date | null>(null);
  const [urlSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasAutoSearched = useRef(false);
  const [searchToken, setSearchToken] = useState<string | null>(null);
  const { addItem } = useCart();
  const resultsRef = useRef<HTMLDivElement>(null);
  const [isPageChanging, setIsPageChanging] = useState(false);
  const [destinationDisplayName, setDestinationDisplayName] = useState<string>("");
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const MAX_RETRIES = 2;
  
  const searchTips = useMemo(() => [
    t('resorts.searchTips.tip1'),
    t('resorts.searchTips.tip2'),
    t('resorts.searchTips.tip3'),
    t('resorts.searchTips.tip4'),
    t('resorts.searchTips.tip5'),
  ], [t]);
  
  // Rotate tips during search
  useEffect(() => {
    if (!isSearching) {
      setCurrentTipIndex(0);
      return;
    }
    
    const interval = setInterval(() => {
      setCurrentTipIndex((prev) => (prev + 1) % searchTips.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [isSearching, searchTips.length]);
  
  const ITEMS_PER_PAGE = 12;
  
  const maxPricePerNight = useMemo(() => {
    if (!searchResults || searchResults.length === 0 || !searchParams) return 500;
    const totalNights = searchParams 
      ? Math.ceil((new Date(searchParams.checkOut).getTime() - new Date(searchParams.checkIn).getTime()) / (1000 * 60 * 60 * 24))
      : 1;
    
    const pricesPerNight = searchResults.flatMap(h => 
      h.Rooms.map(r => {
        const priceEur = r.PriceEur || 0;
        return totalNights > 0 ? priceEur / totalNights : priceEur;
      }).filter((p): p is number => p > 0)
    );
    
    return pricesPerNight.length > 0 ? Math.ceil(Math.max(...pricesPerNight)) : 500;
  }, [searchResults, searchParams]);

  const boardTypes = useMemo(() => {
    if (!searchResults) return [];
    const boards = searchResults.flatMap(
      (h) => h.Rooms?.map((r) => r.BoardDescription).filter(Boolean) || []
    );
    const translatedBoards = boards.map((board) => translateBoardType(board, locale));
    return [...new Set(translatedBoards)].sort();
  }, [searchResults, locale]);

  const starRatings = useMemo(() => {
    if (!searchResults) return [];
    const ratings = searchResults.map(h => (h as any).Category || h.StarRating).filter((r): r is number => r > 0);
    return [...new Set(ratings)].sort((a, b) => b - a);
  }, [searchResults]);

  const destinationCoordinates = useMemo(() => {
    if (!searchResults || searchResults.length === 0) return null;
    
    const hotelsWithCoords = searchResults.filter(h => {
      const lat = h.Position?.Latitude || h.Latitude;
      const lng = h.Position?.Longitude || h.Longitude;
      return lat && lng;
    });
    
    if (hotelsWithCoords.length === 0) return null;
    
    const totalLat = hotelsWithCoords.reduce((sum, h) => {
      return sum + (h.Position?.Latitude || h.Latitude || 0);
    }, 0);
    const totalLng = hotelsWithCoords.reduce((sum, h) => {
      return sum + (h.Position?.Longitude || h.Longitude || 0);
    }, 0);
    
    return {
      lat: totalLat / hotelsWithCoords.length,
      lng: totalLng / hotelsWithCoords.length,
    };
  }, [searchResults]);

  const hotelIds = useMemo(() => {
    return searchResults.map(h => h.HotelId.toString());
  }, [searchResults]);

  const { recommendedHotelIds, hasRecommendedHotels } = useRecommendedHotels(hotelIds);

  const [filters, setFilters] = useState({
    priceRange: [0, 500] as [number, number],
    sortBy: "default",
    boardTypes: [] as string[],
    starRatings: [] as number[],
    cancellationPolicy: "all",
    hotelNameSearch: "",
    recommendedOnly: false,
  });

  const userAdjustedPriceRef = useRef(false);

  useEffect(() => {
    if (!hasSearched) return;
    if (!userAdjustedPriceRef.current) {
      const upper = Math.ceil(maxPricePerNight || 0);
      setFilters((prev) => ({
        ...prev,
        priceRange: [0, upper],
      }));
    }
  }, [maxPricePerNight, hasSearched]);

  // Track previous URL params to detect real changes
  const prevParamsRef = useRef<string>("");

  useEffect(() => {
    const resortDestinationId = urlSearchParams.get('resortDestinationId');
    const resortDestinationName = urlSearchParams.get('resortDestinationName');
    const checkIn = urlSearchParams.get('checkIn');
    const checkOut = urlSearchParams.get('checkOut');
    const adults = urlSearchParams.get('adults');
    const childrenAges = urlSearchParams.get('childrenAges');

    // Build a key from current params to detect changes
    const currentParamsKey = `${resortDestinationId}-${checkIn}-${checkOut}-${adults}-${childrenAges}`;

    if (resortDestinationName) {
      setDestinationDisplayName(decodeURIComponent(resortDestinationName));
    }

    if (resortDestinationId && checkIn && checkOut && adults) {
      const destination = RESORT_DESTINATIONS.find(d => d.id === resortDestinationId);
      if (destination) {
        // Only skip if params haven't changed since last search
        if (prevParamsRef.current === currentParamsKey && hasAutoSearched.current) {
          return;
        }
        
        hasAutoSearched.current = true;
        prevParamsRef.current = currentParamsKey;
        
        const params: ResortSearchParams = {
          resortDestinationId,
          resortDestinationName: resortDestinationName ? decodeURIComponent(resortDestinationName) : destination.name,
          hotelIds: destination.hotelIds,
          checkIn,
          checkOut,
          adults: parseInt(adults),
          childrenAges: childrenAges ? childrenAges.split(',').map(Number).filter(age => !isNaN(age)) : [],
        };
        
        handleSearch(params);
      }
    }
  }, [urlSearchParams]);

  const handleFiltersChange = (next: typeof filters) => {
    if (
      next?.priceRange &&
      (next.priceRange[0] !== filters.priceRange[0] ||
        next.priceRange[1] !== filters.priceRange[1])
    ) {
      userAdjustedPriceRef.current = true;
    }

    startTransition(() => {
      setFilters(next);
    });
  };

  const handleSearch = async (params: ResortSearchParams, isRetry = false) => {
    setIsSearching(true);
    setHasSearched(true);
    setSearchParams(params);
    if (!isRetry) {
      setCurrentPage(1);
      userAdjustedPriceRef.current = false;
      setSearchTimestamp(new Date());
      setRetryAttempt(0);
    }
    
    if (params.resortDestinationName) {
      setDestinationDisplayName(params.resortDestinationName);
    }
    
    const currentAttempt = isRetry ? retryAttempt + 1 : 0;
    if (isRetry) {
      setRetryAttempt(currentAttempt);
      console.log(`Retrying resort search, attempt ${currentAttempt} of ${MAX_RETRIES}`);
    }
    
    try {
      const { data, error } = await supabase.functions.invoke('iterpec-search-resorts', {
        body: {
          hotelIds: params.hotelIds,
          checkIn: params.checkIn,
          checkOut: params.checkOut,
          adults: params.adults,
          childrenAges: params.childrenAges,
        }
      });

      if (error) throw error;

      const response = data as IterpecSearchResponse;
      const hotels = response.Hotels || [];
      
      // If no results and we haven't exceeded max retries, try again
      if (hotels.length === 0 && currentAttempt < MAX_RETRIES) {
        console.log(`No results returned, scheduling retry ${currentAttempt + 1}...`);
        toast({
          title: t('resorts.retrying'),
          description: t('resorts.retryingDescription', { attempt: currentAttempt + 1, max: MAX_RETRIES }),
        });
        
        // Wait 2 seconds before retrying
        await new Promise(resolve => setTimeout(resolve, 2000));
        return handleSearch(params, true);
      }
      
      setSearchResults(hotels);
      setSearchToken(response.Token || null);
      setRetryAttempt(0);
      
      setTimeout(() => {
        if (resultsRef.current) {
          const yOffset = -80;
          const element = resultsRef.current;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
      
      if (hotels.length > 0) {
        toast({
          title: t('resorts.searchComplete'),
          description: t('resorts.resortsFound', { count: hotels.length }),
        });
      } else {
        toast({
          title: t('resorts.noResultsTitle'),
          description: t('resorts.noResultsAfterRetries'),
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error searching resorts:', error);
      
      // On error, retry if we haven't exceeded max retries
      if (currentAttempt < MAX_RETRIES) {
        console.log(`Error occurred, scheduling retry ${currentAttempt + 1}...`);
        toast({
          title: t('resorts.retrying'),
          description: t('resorts.retryingDescription', { attempt: currentAttempt + 1, max: MAX_RETRIES }),
        });
        
        await new Promise(resolve => setTimeout(resolve, 2000));
        return handleSearch(params, true);
      }
      
      toast({
        title: t('errors.generic'),
        description: t('errors.searchFailed'),
        variant: "destructive",
      });
      setSearchResults([]);
      setRetryAttempt(0);
    } finally {
      setIsSearching(false);
    }
  };

  const filteredAndSortedHotels = useMemo(() => {
    let filtered = searchResults;

    const totalNights = searchParams 
      ? Math.ceil((new Date(searchParams.checkOut).getTime() - new Date(searchParams.checkIn).getTime()) / (1000 * 60 * 60 * 24))
      : 1;

    if (filters.recommendedOnly) {
      filtered = filtered.filter(hotel => 
        recommendedHotelIds.has(hotel.HotelId.toString())
      );
    }

    if (filters.hotelNameSearch && filters.hotelNameSearch.trim()) {
      const searchTerm = filters.hotelNameSearch.toLowerCase().trim();
      filtered = filtered.filter(hotel => 
        hotel.Name.toLowerCase().includes(searchTerm)
      );
    }

    filtered = filtered.filter(hotel => {
      if (!hotel.Rooms || hotel.Rooms.length === 0) return true;
      const prices = hotel.Rooms.map(r => r.PriceEur).filter((p): p is number => p !== undefined && p > 0);
      if (prices.length === 0) return true;
      const minRoomPriceTotal = Math.min(...prices);
      const minRoomPricePerNight = totalNights > 0 ? minRoomPriceTotal / totalNights : minRoomPriceTotal;
      return minRoomPricePerNight >= filters.priceRange[0] && minRoomPricePerNight <= filters.priceRange[1];
    });

    if (filters.starRatings.length > 0) {
      filtered = filtered.filter(hotel => {
        const rating = (hotel as any).Category || hotel.StarRating;
        return filters.starRatings.includes(rating);
      });
    }

    if (filters.boardTypes.length > 0) {
      filtered = filtered.filter((hotel) => {
        if (!hotel.Rooms) return false;
        return hotel.Rooms.some((room) => {
          if (!room.BoardDescription) return false;
          const translatedBoard = translateBoardType(room.BoardDescription, locale);
          return filters.boardTypes.includes(translatedBoard);
        });
      });
    }

    if (filters.cancellationPolicy !== "all") {
      filtered = filtered.filter(hotel => {
        if (!hotel.Rooms) return false;
        
        return hotel.Rooms.some(room => {
          const hasPolicy = room.CancellationPolicies && room.CancellationPolicies.length > 0;
          if (!hasPolicy) return filters.cancellationPolicy === "refundable";
          
          const firstPolicy = room.CancellationPolicies![0];
          const parseDateOnly = (input: string) => {
            const match = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (match) {
              const [, y, m, d] = match;
              return new Date(Number(y), Number(m) - 1, Number(d));
            }
            const d = new Date(input);
            return new Date(d.getFullYear(), d.getMonth(), d.getDate());
          };

          const policyStartDate = parseDateOnly(firstPolicy.StartDate);
          const baseSearch = searchTimestamp ? new Date(searchTimestamp) : new Date();
          const searchDate = new Date(baseSearch.getFullYear(), baseSearch.getMonth(), baseSearch.getDate());
          const searchDatePlus5 = new Date(searchDate);
          searchDatePlus5.setDate(searchDatePlus5.getDate() + 5);

          const isNonRefundable = policyStartDate <= searchDatePlus5;
          
          if (filters.cancellationPolicy === "non-refundable") {
            return isNonRefundable;
          } else {
            return !isNonRefundable;
          }
        });
      });
    }

    filtered.sort((a, b) => {
      const aIsRecommended = recommendedHotelIds.has(a.HotelId.toString());
      const bIsRecommended = recommendedHotelIds.has(b.HotelId.toString());
      
      if (!filters.recommendedOnly && aIsRecommended !== bIsRecommended) {
        return aIsRecommended ? -1 : 1;
      }
      
      const aPrices = a.Rooms?.map(r => r.PriceEur).filter((p): p is number => p !== undefined && p > 0) || [];
      const bPrices = b.Rooms?.map(r => r.PriceEur).filter((p): p is number => p !== undefined && p > 0) || [];
      const aPrice = aPrices.length > 0 ? Math.min(...aPrices) : 0;
      const bPrice = bPrices.length > 0 ? Math.min(...bPrices) : 0;
      
      switch (filters.sortBy) {
        case "price_asc":
          return aPrice - bPrice;
        case "price_desc":
          return bPrice - aPrice;
        case "name_asc":
          return a.Name.localeCompare(b.Name);
        case "name_desc":
          return b.Name.localeCompare(a.Name);
        default:
          return 0;
      }
    });

    return filtered;
  }, [searchResults, filters, searchParams, searchTimestamp, locale, recommendedHotelIds]);

  const paginatedHotels = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredAndSortedHotels.slice(startIndex, endIndex);
  }, [filteredAndSortedHotels, currentPage]);

  const totalPages = Math.ceil(filteredAndSortedHotels.length / ITEMS_PER_PAGE);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  useEffect(() => {
    if (!hasSearched || currentPage === 1) return;
    
    setIsPageChanging(true);
    
    setTimeout(() => {
      if (resultsRef.current) {
        const yOffset = -80;
        const element = resultsRef.current;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
      
      setTimeout(() => setIsPageChanging(false), 50);
    }, 100);
  }, [currentPage, hasSearched]);

  const baseUrl = getLocalizedUrl(locale as LocaleKey, '/');
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: t('nav.home'), url: baseUrl },
    { name: t('resorts.breadcrumb'), url: getLocalizedUrl(locale as LocaleKey, '/resorts') },
    ...(destinationDisplayName ? [{ name: destinationDisplayName, url: '' }] : [])
  ]);
  const organizationSchema = getOrganizationSchema(locale as LocaleKey);
  const webSiteSchema = getWebSiteSchema(locale as LocaleKey);
  const resortServiceSchema = getResortServiceSchema(locale as LocaleKey, destinationDisplayName || undefined);

  const renderResortCard = (hotel: IterpecHotel) => {
    const prices = hotel.Rooms?.map(r => r.PriceEur).filter((p): p is number => p !== undefined && p > 0) || [];
    const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
    const images = hotel.ThumbUrl ? [hotel.ThumbUrl] : [];
    
    const cheapestRoomIndex = hotel.Rooms?.findIndex(r => {
      const roomPrice = r.PriceEur;
      if (!roomPrice) return false;
      return roomPrice === minPrice;
    }) ?? -1;
    
    const cheapestRoom = cheapestRoomIndex >= 0 ? hotel.Rooms?.[cheapestRoomIndex] : null;
    
    const totalNights = searchParams 
      ? Math.ceil((new Date(searchParams.checkOut).getTime() - new Date(searchParams.checkIn).getTime()) / (1000 * 60 * 60 * 24))
      : 1;
    const pricePerNight = totalNights > 0 ? minPrice / totalNights : minPrice;

    const cancellationInfo = cheapestRoom?.CancellationPolicies && cheapestRoom.CancellationPolicies.length > 0
      ? (() => {
          const firstPolicy = cheapestRoom.CancellationPolicies[0];

          const parseDateOnly = (input: string) => {
            const match = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (match) {
              const [, y, m, d] = match;
              return new Date(Number(y), Number(m) - 1, Number(d));
            }
            const d = new Date(input);
            return new Date(d.getFullYear(), d.getMonth(), d.getDate());
          };

          const policyStartDate = parseDateOnly(firstPolicy.StartDate);
          const baseSearch = searchTimestamp ? new Date(searchTimestamp) : new Date();
          const searchDate = new Date(baseSearch.getFullYear(), baseSearch.getMonth(), baseSearch.getDate());
          const searchDatePlus5 = new Date(searchDate);
          searchDatePlus5.setDate(searchDatePlus5.getDate() + 5);

          let text = '';
          let type: 'non-refundable' | 'refundable' | 'refundable-until' = 'refundable';

          if (policyStartDate <= searchDatePlus5) {
            text = t('hotels.nonRefundable');
            type = 'non-refundable';
          } else {
            text = t('hotels.refundable');
            type = 'refundable';
          }

          return { text, type };
        })()
      : null;
    
    const numNights = searchParams 
      ? Math.ceil((new Date(searchParams.checkOut).getTime() - new Date(searchParams.checkIn).getTime()) / (1000 * 60 * 60 * 24))
      : 1;
    
    const params = new URLSearchParams({
      checkIn: searchParams?.checkIn || '',
      numNights: numNights.toString(),
      adults: searchParams?.adults?.toString() || '',
    });
    
    // Use the hotel's actual Destination.Id from API response (numeric ID required by Iterpec)
    if (hotel.Destination?.Id) {
      params.append('destinationId', hotel.Destination.Id.toString());
    }
    
    // Keep destination name for display purposes
    if (searchParams?.resortDestinationName) {
      params.append('destinationName', searchParams.resortDestinationName);
    }
    
    if (searchParams?.childrenAges && searchParams.childrenAges.length > 0) {
      params.append('childrenAges', searchParams.childrenAges.join(','));
    }
    
    // Mark origin as resorts so the back button knows where to return
    params.append('origin', 'resorts');
    if (searchParams?.resortDestinationId) {
      params.append('resortDestinationId', searchParams.resortDestinationId);
    }
    
    // Reutilizamos a página de detalhe de hotel
    const detailUrl = `/hoteles/${hotel.HotelId}?${params.toString()}`;
    
    const handleAddToCart = async () => {
      if (!cheapestRoom) {
        toast({
          title: t('common.error'),
          description: t('hotels.noRoomsAvailable'),
          variant: "destructive",
        });
        return;
      }

      try {
        const { data: productData, error: productError } = await supabase.functions.invoke(
          'save-hotel-product',
          {
            body: {
              hotelId: hotel.HotelId,
              roomId: cheapestRoom.Id.toString(),
              roomDescription: cheapestRoom.RoomDescription || 'Quarto',
              bedDescription: cheapestRoom.BedDescription || null,
              boardDescription: cheapestRoom.BoardDescription || null,
              priceEur: minPrice,
              hotelName: hotel.Name,
            },
          }
        );

        if (productError || !productData?.success) {
          throw new Error('Failed to save product');
        }

        const checkInDate = new Date(searchParams?.checkIn || '');
        const checkOutDate = new Date(searchParams?.checkOut || '');
        const cartNumNights = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24));

        const cancellationPolicy = cheapestRoom.CancellationPolicies?.[0];
        const cancellationType = cancellationPolicy ? 'Reembolsable' : 'No reembolsable';
        const cancellationText = cancellationPolicy 
          ? `Reembolsable hasta ${new Date(cancellationPolicy.StartDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}`
          : 'No reembolsable';

        await addItem({
          productId: productData.productId,
          bookingDetails: {
            type: 'resort',
            hotelId: hotel.HotelId,
            hotelName: hotel.Name,
            hotelAddress: hotel.Address || '',
            roomId: cheapestRoom.Id.toString(),
            roomName: cheapestRoom.RoomDescription,
            roomDescription: cheapestRoom.RoomDescription,
            bedDescription: cheapestRoom.BedDescription || null,
            boardType: cheapestRoom.BoardDescription,
            boardDescription: cheapestRoom.BoardDescription,
            checkIn: searchParams?.checkIn,
            checkOut: searchParams?.checkOut,
            numNights: cartNumNights.toString(),
            adults: searchParams?.adults,
            childrenAges: searchParams?.childrenAges,
            priceEur: minPrice,
            propertyTaxesEur: cheapestRoom.PropertyTaxesEur,
            cancellationPolicies: cheapestRoom.CancellationPolicies,
            cancellationType,
            cancellationPolicy: cancellationText,
            cancellationStartDate: cancellationPolicy?.StartDate || null,
          },
          searchToken: searchToken || undefined,
        });
      } catch (error) {
        console.error('Error adding to cart:', error);
        toast({
          title: t('common.error'),
          description: t('cart.updateError'),
          variant: "destructive",
        });
      }
    };
    
    const isRecommended = recommendedHotelIds.has(hotel.HotelId.toString());
    
    return (
      <ProductCard 
        key={hotel.HotelId}
        detailsUrl={detailUrl}
        onAddToCart={handleAddToCart}
        isRecommended={isRecommended}
        product={{
          id: hotel.HotelId.toString(),
          name: hotel.Name,
          name_es: hotel.Name,
          description: hotel.Address,
          description_es: hotel.Address,
          product_type: "hotel", // Resorts use hotel type for now
          price_eur: minPrice,
          location: hotel.Address,
          images: images,
          metadata: {
            starRating: (hotel as any).Category || hotel.StarRating,
            rooms: hotel.Rooms,
            position: hotel.Position,
            roomDescription: cheapestRoom?.RoomDescription,
            boardDescription: cheapestRoom?.BoardDescription,
            cancellationInfo,
            pricePerNight,
            totalNights,
            propertyTaxesEur: cheapestRoom?.PropertyTaxesEur,
          },
          provider: "iterpec",
          is_active: true,
        }} 
      />
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <HreflangTags path="/resorts" />
<SchemaOrg schema={[organizationSchema, webSiteSchema, breadcrumbSchema, resortServiceSchema]} />
      <Navbar />
      
      {/* Show Hero when no search has been performed */}
      {!hasSearched && <ResortHero />}
      
      <main id="main-content" className="container mx-auto px-4 py-8" role="main">
        {/* Page H1 - always visible for SEO */}
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          {destinationDisplayName
            ? t('resorts.pageTitleWithDestination', { destination: destinationDisplayName })
            : t('resorts.pageTitle')}
        </h1>

        {/* Dynamic Breadcrumbs */}
        <Breadcrumb 
          items={
            destinationDisplayName 
              ? [
                  { label: t('resorts.breadcrumb'), href: '/resorts' },
                  { label: destinationDisplayName }
                ]
              : [{ label: t('resorts.breadcrumb') }]
          } 
        />

        {/* Inline Search Form - shown after search */}
        {hasSearched && (
          <div className="mb-8 p-6 bg-primary/5 rounded-xl border border-border">
            <ResortSearchForm />
          </div>
        )}

        {!hasSearched ? (
          /* Featured destinations when no search performed */
          <section className="py-8">
            <h2 className="text-2xl font-bold mb-8 text-center">{t('resorts.featuredDestinations')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {RESORT_DESTINATIONS.map((dest) => (
                <a 
                  key={dest.id}
                  href={`/resorts/${dest.id}`}
                  className="relative group overflow-hidden rounded-xl cursor-pointer shadow-lg hover:shadow-xl transition-shadow block"
                >
                  <img
                    src={getMobileOptimizedUrl(dest.image, 640, 60)}
                    srcSet={`${getMobileOptimizedUrl(dest.image, 400, 60)} 400w, ${getMobileOptimizedUrl(dest.image, 640, 60)} 640w, ${getMobileOptimizedUrl(dest.image, 800, 50)} 800w`}
                    sizes="(max-width: 768px) 100vw, 33vw"
                    alt={t(dest.nameKey) || dest.name}
                    loading="lazy"
                    decoding="async"
                    className="h-64 w-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-xl font-bold text-primary-foreground">
                      {t(dest.nameKey) || dest.name}
                    </h3>
                    <p className="text-sm text-primary-foreground/80 mt-1">
                      {t('common.explore')} →
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </section>
        ) : (
          <div ref={resultsRef}>
            {/* Destination Title */}
            {destinationDisplayName && (
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                {destinationDisplayName}
              </h2>
            )}
            
            {/* Results Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div className="flex items-center gap-2">
                <Palmtree className="h-5 w-5 text-primary shrink-0" />
                <span className="text-sm sm:text-base font-medium text-foreground">
                  {isSearching ? (
                    t('resorts.searching')
                  ) : (
                    `${filteredAndSortedHotels.length} ${t('resorts.propertiesFound')}`
                  )}
                </span>
              </div>
              
              <div className="flex items-center gap-2 flex-wrap">
                {/* Mobile Filter Button */}
                {searchResults.length > 0 && (
                  <div className="lg:hidden">
                    <HotelFiltersSidebar
                      filters={filters}
                      onFiltersChange={handleFiltersChange}
                      maxPrice={maxPricePerNight}
                      boardTypes={boardTypes}
                      starRatings={starRatings}
                      resultsCount={filteredAndSortedHotels.length}
                      destinationCoordinates={destinationCoordinates}
                      onOpenMap={() => setIsMapOpen(true)}
                      hasRecommendedHotels={hasRecommendedHotels}
                    />
                  </div>
                )}
                
                {/* Map button */}
                {searchResults.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsMapOpen(true)}
                    className="gap-2"
                    aria-label={t('hotels.viewMap')}
                  >
                    <Map className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('hotels.viewMap')}</span>
                  </Button>
                )}
                
                {/* Sort dropdown */}
                <Select
                  value={filters.sortBy}
                  onValueChange={(value) => handleFiltersChange({ ...filters, sortBy: value })}
                >
                  <SelectTrigger className="w-[130px] sm:w-[160px] h-9 text-sm">
                    <SelectValue placeholder={t('filters.sortBy')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">{t('filters.default')}</SelectItem>
                    <SelectItem value="price_asc">{t('filters.priceLowToHigh')}</SelectItem>
                    <SelectItem value="price_desc">{t('filters.priceHighToLow')}</SelectItem>
                    <SelectItem value="name_asc">{t('filters.nameAZ')}</SelectItem>
                    <SelectItem value="name_desc">{t('filters.nameZA')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Main Content with Sidebar */}
            <div className="flex gap-8">
              {/* Desktop Filters Sidebar */}
              {searchResults.length > 0 && (
                <div className="hidden lg:block">
                  <HotelFiltersSidebar
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    maxPrice={maxPricePerNight}
                    boardTypes={boardTypes}
                    starRatings={starRatings}
                    resultsCount={filteredAndSortedHotels.length}
                    destinationCoordinates={destinationCoordinates}
                    onOpenMap={() => setIsMapOpen(true)}
                    hasRecommendedHotels={hasRecommendedHotels}
                  />
                </div>
              )}

              {/* Results Grid */}
              <div className="flex-1 min-w-0">
                {isSearching ? (
                  <div className="space-y-8">
                    <div className="flex flex-col items-center justify-center py-12 px-4">
                      <div className="relative">
                        <div className="h-16 w-16 animate-spin rounded-full border-4 border-primary/30 border-t-primary"></div>
                        <Palmtree className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-6 w-6 text-primary" />
                      </div>
                      <p className="mt-6 text-xl font-semibold text-foreground">
                        {retryAttempt > 0 ? t('resorts.retrying') : t('resorts.searching')}
                      </p>
                      
                      {/* Retry indicator */}
                      {retryAttempt > 0 && (
                        <div className="mt-3 flex items-center gap-2 text-amber-600 dark:text-amber-400">
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          <span className="text-sm font-medium">
                            {t('resorts.retryingDescription', { attempt: retryAttempt, max: MAX_RETRIES })}
                          </span>
                        </div>
                      )}
                      
                      <div className="mt-3 max-w-md text-center">
                        <p className="text-sm text-muted-foreground bg-muted/50 rounded-lg px-4 py-3 border border-border">
                          ⏳ {t('resorts.searchingSubtitle')}
                        </p>
                      </div>
                      
                      {/* Travel Tips */}
                      <div className="mt-6 max-w-lg w-full">
                        <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 transition-all duration-500">
                          <div className="flex items-start gap-3">
                            <div className="shrink-0 mt-0.5">
                              <Lightbulb className="h-5 w-5 text-primary" />
                            </div>
                            <div className="min-h-[3rem] flex items-center">
                              <p className="text-sm text-foreground/80 animate-fade-in" key={currentTipIndex}>
                                {searchTips[currentTipIndex]}
                              </p>
                            </div>
                          </div>
                          <div className="flex justify-center gap-1.5 mt-3">
                            {searchTips.map((_, index) => (
                              <div 
                                key={index}
                                className={`h-1.5 rounded-full transition-all duration-300 ${
                                  index === currentTipIndex 
                                    ? 'w-4 bg-primary' 
                                    : 'w-1.5 bg-primary/30'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
                      {[1, 2, 3, 4, 5, 6].map((i) => (
                        <div key={i} className="space-y-3">
                          <Skeleton className="h-40 sm:h-48 w-full rounded-lg" />
                          <Skeleton className="h-5 w-3/4" />
                          <Skeleton className="h-4 w-1/2" />
                          <Skeleton className="h-4 w-full" />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : filteredAndSortedHotels.length === 0 ? (
                  <div className="rounded-lg border border-border bg-card p-8 sm:p-12 text-center">
                    <Palmtree className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                    <p className="text-base sm:text-lg text-muted-foreground">
                      {t('resorts.noResults')}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 ${!isPageChanging ? 'animate-fade-in' : 'opacity-0'}`}>
                      {paginatedHotels.map(renderResortCard)}
                    </div>
                    
                    {totalPages > 1 && (
                      <div className="mt-8 flex justify-center">
                        <Pagination>
                          <PaginationContent>
                            <PaginationItem>
                              <PaginationPrevious 
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (currentPage > 1) {
                                    setCurrentPage(currentPage - 1);
                                  }
                                }}
                                className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                              />
                            </PaginationItem>
                            
                            {[...Array(totalPages)].map((_, i) => {
                              const pageNum = i + 1;
                              if (
                                pageNum === 1 ||
                                pageNum === totalPages ||
                                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                              ) {
                                return (
                                  <PaginationItem key={pageNum}>
                                    <PaginationLink
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setCurrentPage(pageNum);
                                      }}
                                      isActive={currentPage === pageNum}
                                    >
                                      {pageNum}
                                    </PaginationLink>
                                  </PaginationItem>
                                );
                              } else if (
                                pageNum === currentPage - 2 ||
                                pageNum === currentPage + 2
                              ) {
                                return (
                                  <PaginationItem key={pageNum}>
                                    <PaginationEllipsis />
                                  </PaginationItem>
                                );
                              }
                              return null;
                            })}
                            
                            <PaginationItem>
                              <PaginationNext 
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (currentPage < totalPages) {
                                    setCurrentPage(currentPage + 1);
                                  }
                                }}
                                className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                              />
                            </PaginationItem>
                          </PaginationContent>
                        </Pagination>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Map Modal - Lazy loaded */}
      <LazyHotelsMapModal
        isOpen={isMapOpen}
        onClose={() => setIsMapOpen(false)}
        hotels={filteredAndSortedHotels}
      />

      <Footer />
    </div>
  );
};

export default Resorts;
